import { motion } from "framer-motion";
import { HtmlContent } from "../../../../common/components/HtmlContent";
import { SEVERITIES } from "../../../../common/constants";
import { Severity } from "../../../../common/types";

interface AnnouncementBannerProps {
    title: string;
    description: string;
    severity?: Severity;
    onClose: () => void;
}

const BANNER_SEVERITY_CONFIG = {
    [SEVERITIES.WARNING]: {
        bg: "bg-warning-500",
        icon: "pi pi-exclamation-triangle",
        iconColor: "text-warning-900 dark:text-warning-100",
        textColor: "text-warning-900 dark:text-warning-100",
        hoverColor: "hover:text-warning-900/70 dark:hover:text-warning-100/70",
    },
    [SEVERITIES.INFO]: {
        bg: "bg-info-500",
        icon: "pi pi-info-circle",
        iconColor: "text-info-750",
        textColor: "text-info-750",
        hoverColor: "hover:text-info-650",
    },
    [SEVERITIES.ERROR]: {
        bg: "bg-error-500",
        icon: "pi pi-exclamation-circle",
        iconColor: "text-error-750",
        textColor: "text-error-750",
        hoverColor: "hover:text-error-650",
    },
    [SEVERITIES.SUCCESS]: {
        bg: "bg-success-500",
        icon: "pi pi-check-circle",
        iconColor: "text-success-750",
        textColor: "text-success-750",
        hoverColor: "hover:text-success-650",
    },
};

const AnnouncementBanner = (props: AnnouncementBannerProps) => {
    const {
        title,
        description,
        severity = SEVERITIES.WARNING,
        onClose,
    } = props;

    const config =
        BANNER_SEVERITY_CONFIG[severity] ??
        BANNER_SEVERITY_CONFIG[SEVERITIES.INFO];

    return (
        <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="relative z-30000 overflow-hidden"
        >
            <div className={`flex items-start px-3.5 py-2.5 ${config.bg}`}>
                <div className="flex flex-1 items-start justify-center gap-1.5">
                    <i
                        className={`${config.icon} ${config.iconColor} shrink-0 mt-1`}
                    />
                    <span className={`${config.textColor} flex`}>
                        <span className="font-bold">{title}</span>
                        {description && (
                            <HtmlContent
                                html={description}
                                className="ml-1.5"
                            />
                        )}
                    </span>
                </div>
                <button
                    className={`shrink-0 py-1 px-3.5 -mr-3.5 ${config.textColor} ${config.hoverColor} transition-colors`}
                    onClick={onClose}
                >
                    <i className="pi pi-times text-sm" />
                </button>
            </div>
        </motion.div>
    );
};

export default AnnouncementBanner;
